import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/store';
import {
  compileStoryboard,
  type ClipRequestSummary,
  type CompiledClipPrompt,
  type ShotPlan,
  type StoryboardBrandInput,
  type StoryboardPromotion,
  type VideoAspectRatio,
} from '@/store/slices/creativeJobs';
import { Icon, Text } from '@/nova/components';

import { compileKeyOf, shotPlanProblem } from '../../storyboard';
import { VIDEO_MODELS } from '../../video-models';
import {
  Block,
  BlockLabel,
  Body,
  FactTerm,
  FactValue,
  Facts,
  Note,
  PromptText,
  Root,
  Toggle,
} from './FinalPromptPanel.styles';

/** Idle time after the last plan / model / length / aspect change before recompiling (the compile is free and
 *  synchronous, so this only spares the backend a call per keystroke). */
const RECOMPILE_DEBOUNCE_MS = 600;

/** A compiled prompt together with the compile input it belongs to (see {@link compileKeyOf}). */
export interface KeyedCompiledPrompt {
  key: string;
  compiled: CompiledClipPrompt;
}

export interface FinalPromptPanelProps {
  tenantId?: number;
  /** The brand's site domain (the generate route's `?domain=`). */
  domain?: string;
  /** The request's required non-empty top-level prompt (owner prompt, else the template title). */
  prompt: string;
  /** The subject's aspect-appropriate key visual (the envelope `base_image_url`). */
  baseImageUrl?: string;
  aspect: VideoAspectRatio;
  videoModel: string;
  /** The clip length L. */
  targetDurationSeconds?: number;
  plan?: ShotPlan;
  /** The ad's ONE subject. */
  promotion?: StoryboardPromotion;
  brand: StoryboardBrandInput;
  /** The compose's own compiled prompt: shown as-is while its key still matches, so no compile is needed right
   *  after a compose. */
  seed?: KeyedCompiledPrompt | null;
}

type RequestFact = { term: string; value: string };

/** Owner-readable rows for the adapter's request summary (`null` / absent = the field is not sent), already
 *  translated (each copy sits in its own `t()` call so the extractor harvests it) and rendered raw. */
const requestFacts = (
  req: ClipRequestSummary | undefined,
  modelId: string,
  t: (key: string) => string,
): RequestFact[] => {
  const model = req?.model || modelId;
  return [
    { term: t('Model'), value: VIDEO_MODELS.find((m) => m.id === model)?.label ?? model },
    { term: t('Duration'), value: req?.duration_seconds != null ? `${req.duration_seconds}s` : t('Set by the prompt') },
    { term: t('Aspect ratio'), value: req?.aspect_ratio ? req.aspect_ratio : t('From the opening frame') },
    { term: t('Resolution'), value: req?.resolution ? req.resolution : t('Model default') },
    { term: t('Audio'), value: req?.generate_audio === false ? t('Off') : t('Music and sound effects') },
    { term: t('Opening frame'), value: req?.opening_frame === false ? t('No, text only') : t('Yes') },
  ];
};

/**
 * The read-only "View final prompt" panel of a single-clip (v2) ad: the exact prompt, negative prompt and
 * request the backend adapter will send to the chosen video model. Collapsed by default. It recompiles via
 * {@link compileStoryboard} (free, no LLM), debounced, whenever the plan / model / length / aspect change, and
 * shows the compose's own compiled prompt straight away after a compose (the `seed`).
 */
export const FinalPromptPanel = ({
  tenantId,
  domain,
  prompt,
  baseImageUrl,
  aspect,
  videoModel,
  targetDurationSeconds,
  plan,
  promotion,
  brand,
  seed,
}: FinalPromptPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = React.useState(false);
  const [result, setResult] = React.useState<KeyedCompiledPrompt | null>(null);
  const [failedKey, setFailedKey] = React.useState<string | null>(null);
  // Bumped to re-run a compile that failed for the current key (once per expand of the panel, see `toggle`).
  const [retry, setRetry] = React.useState(0);

  const key = compileKeyOf(plan, videoModel, targetDurationSeconds, aspect);
  const problem = shotPlanProblem(plan, targetDurationSeconds);
  const valid = problem === null;
  const current = seed && seed.key === key ? seed : result && result.key === key ? result : null;

  // The envelope fields (prompt, key visual, subject, brand) do not change the adapter's output, so the compile
  // re-runs on the compile KEY only; a ref hands the effect their latest values without re-firing it on each one.
  const argsRef = React.useRef({
    tenantId,
    domain,
    prompt,
    baseImageUrl,
    promotion,
    brand,
    plan,
    videoModel,
    targetDurationSeconds,
    aspect,
  });
  argsRef.current = {
    tenantId,
    domain,
    prompt,
    baseImageUrl,
    promotion,
    brand,
    plan,
    videoModel,
    targetDurationSeconds,
    aspect,
  };

  // Syncs the displayed prompt with the backend compiler: debounced, aborted when superseded or on unmount.
  React.useEffect(() => {
    if (!valid || current) return undefined;
    const a = argsRef.current;
    if (!a.tenantId || !a.plan || !a.promotion || !a.baseImageUrl || !a.targetDurationSeconds) return undefined;
    const { tenantId: tid, plan: p, promotion: promo, baseImageUrl: img, targetDurationSeconds: L } = a;
    const ctrl = new AbortController();
    const handle = window.setTimeout(() => {
      void dispatch(
        compileStoryboard({
          tenantId: tid,
          domain: a.domain,
          prompt: a.prompt,
          baseImageUrl: img,
          aspectRatio: a.aspect,
          videoModel: a.videoModel,
          targetDurationSeconds: L,
          shotPlan: p,
          promotion: promo,
          brand: a.brand,
          signal: ctrl.signal,
        }),
      )
        .unwrap()
        .then((compiled) => {
          if (ctrl.signal.aborted) return;
          setResult({ key, compiled });
          setFailedKey(null);
        })
        .catch(() => {
          if (!ctrl.signal.aborted) setFailedKey(key);
        });
    }, RECOMPILE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
      ctrl.abort();
    };
    // `current` is derived from `key` (+ seed/result); keying on it would re-run after every landed result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, valid, dispatch, retry]);

  // Expanding the panel after the compile for the CURRENT input failed retries it once (a transient failure would
  // otherwise stick until the plan / model / length / aspect changed).
  const toggle = (): void => {
    if (!open && failedKey === key) {
      setFailedKey(null);
      setRetry((n) => n + 1);
    }
    setOpen(!open);
  };

  // The last compiled prompt (possibly for an older input) stays visible, softened, while a recompile is pending,
  // and stays (as the last good prompt) when a recompile fails.
  const shown = current ?? result;
  const pending = valid && !current && failedKey !== key;
  const facts = shown ? requestFacts(shown.compiled.request, videoModel, (k) => t(k)) : [];

  return (
    <Root>
      <Toggle type="button" $open={open} aria-expanded={open} onClick={toggle}>
        <Icon name="chevron-down" size={14} />
        {open ? <Text tag="span" text="Hide final prompt" /> : <Text tag="span" text="View final prompt" />}
      </Toggle>
      {open ? (
        <Body $pending={pending && shown !== null}>
          {problem === 'timing' ? (
            <Note text="The shot list no longer matches the clip length. Regenerate the storyboard to fix it." />
          ) : problem === 'empty' ? (
            <Note text="Fill in every shot's action and camera, and the music line. Quoted text is removed, so it doesn't count." />
          ) : failedKey === key ? (
            // A compile the backend refused (409 not configured / 400 invalid plan) is not an owner error: say so
            // quietly and keep showing the last prompt that did build.
            <Note text="Preview unavailable" />
          ) : null}
          {valid && !shown && failedKey !== key ? <Note text="Building the final prompt…" /> : null}
          {valid && shown ? (
            <>
              <Block>
                <BlockLabel text="Prompt" />
                <PromptText raw text={shown.compiled.prompt} />
              </Block>
              {shown.compiled.negativePrompt ? (
                <Block>
                  <BlockLabel text="Negative prompt" />
                  <PromptText raw text={shown.compiled.negativePrompt} />
                </Block>
              ) : null}
              <Block>
                <BlockLabel text="Request" />
                <Facts>
                  {facts.map((f) => (
                    <React.Fragment key={f.term}>
                      <FactTerm raw text={f.term} />
                      <FactValue raw text={f.value} />
                    </React.Fragment>
                  ))}
                </Facts>
              </Block>
            </>
          ) : null}
        </Body>
      ) : null}
    </Root>
  );
};

export default FinalPromptPanel;
